import React, { useRef, useState } from "react";
import { Upload, FileText, Loader2, Sparkles, CheckCheck, Plus, Replace } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * <AffindaResumeParser />
 *
 * Uploads a resume via POST /api/affinda/parse-resume and lets the user
 * merge/replace parsed skills, experience, and education into their existing
 * Talent Passport fields. Optionally auto-fills name + phone from the parsed
 * personal info block when the "Also auto-fill personal info" toggle is on.
 *
 * Props:
 *   currentSkills / currentExperience / currentEducation: string[]
 *   currentName / currentPhone: string
 *   onMergeSkills / onMergeExperience / onMergeEducation: (arr: string[]) => void
 *   onAutoFillPersonal: ({ name?: string, phone?: string }) => void
 */
export function AffindaResumeParser({
    currentSkills = [],
    currentExperience = [],
    currentEducation = [],
    currentName = "",
    currentPhone = "",
    onMergeSkills,
    onMergeExperience,
    onMergeEducation,
    onAutoFillPersonal,
}) {
    const [state, setState] = useState({ phase: "idle", filename: "", parsed: null });
    const [autoPersonal, setAutoPersonal] = useState(true);
    const inputRef = useRef(null);

    const onFile = async (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("File must be under 10MB.");
            return;
        }
        setState({ phase: "uploading", filename: file.name, parsed: null });
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/affinda/parse-resume", form, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60_000,
            });
            // Affinda now returns immediately with ``ready=false`` — poll
            // ``/affinda/documents/{identifier}`` until parsing finishes.
            // Bounded at ~90s (45 iters * 2s) so a truly stuck doc doesn't
            // hang the UI forever. Previous ``wait=true`` flow tripped
            // Cloudflare's 60s edge timeout on longer PDFs.
            let parsed = data;
            if (!parsed.ready && parsed.identifier) {
                setState({ phase: "uploading", filename: file.name, parsed: null, pollingHint: "Parsing your resume — this usually takes 5–20 seconds…" });
                for (let i = 0; i < 45; i++) {
                    await new Promise((res) => setTimeout(res, 2000));
                    try {
                        const { data: doc } = await api.get(`/affinda/documents/${parsed.identifier}`, { timeout: 20_000 });
                        if (doc.ready || doc.failed) { parsed = doc; break; }
                    } catch { /* transient — keep polling */ }
                }
                if (!parsed.ready) {
                    setState({ phase: "idle", filename: "", parsed: null });
                    toast.error("Resume parsing is taking longer than usual. Try again in a moment, or fill the fields manually.");
                    return;
                }
                if (parsed.failed) {
                    setState({ phase: "idle", filename: "", parsed: null });
                    toast.error("We couldn't parse this resume. Try a different file or fill the fields manually.");
                    return;
                }
            }
            setState({ phase: "ready", filename: file.name, parsed });

            // Auto-fill personal info if the toggle is on AND we don't already
            // have that value. Never overwrite something the user has typed —
            // that would surprise them.
            if (autoPersonal && onAutoFillPersonal) {
                const patch = {};
                if (parsed.name && !currentName.trim()) patch.name = parsed.name;
                const firstPhone = Array.isArray(parsed.phones) && parsed.phones[0];
                if (firstPhone && !currentPhone.trim()) patch.phone = firstPhone;
                if (Object.keys(patch).length) {
                    onAutoFillPersonal(patch);
                    toast.success(`Auto-filled: ${Object.keys(patch).join(", ")}`);
                }
            }
            toast.success("Resume parsed — review the suggestions below.");
        } catch (err) {
            setState({ phase: "idle", filename: "", parsed: null });
            toast.error(formatApiError(err));
        }
    };

    const experienceLines = (state.parsed?.work_experience || [])
        .map((w) => {
            const parts = [w.title, w.organization && `@ ${w.organization}`].filter(Boolean).join(" ");
            const d = w.dates || {};
            const range = [d.startDate, d.endDate || (d.isCurrent ? "Now" : "")].filter(Boolean).join(" – ");
            return [parts, range && `· ${range}`].filter(Boolean).join(" ");
        })
        .filter((line) => line.length > 0);

    const educationLines = (state.parsed?.education || [])
        .map((e) => {
            const parts = [e.accreditation?.education, e.organization && `@ ${e.organization}`].filter(Boolean).join(" ");
            const range = e.dates?.startDate || e.dates?.completionDate ? `· ${e.dates?.startDate || ""} – ${e.dates?.completionDate || e.dates?.endDate || ""}` : "";
            return [parts, range].filter(Boolean).join(" ");
        })
        .filter((line) => line.length > 0);

    // Case-insensitive dedupe that preserves the first occurrence's casing.
    // Prevents "Python" + "python" from both being stored.
    const dedupeMerge = (a, b) => {
        const seen = new Set();
        const out = [];
        for (const item of [...(a || []), ...b]) {
            if (!item || !item.trim()) continue;
            const key = item.trim().toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(item.trim());
        }
        return out;
    };

    const merge = (kind) => {
        if (kind === "skills") onMergeSkills(dedupeMerge(currentSkills, state.parsed.skills || []));
        if (kind === "experience") onMergeExperience(dedupeMerge(currentExperience, experienceLines));
        if (kind === "education") onMergeEducation(dedupeMerge(currentEducation, educationLines));
        toast.success(`Added to your passport.`);
    };
    const replace = (kind) => {
        if (kind === "skills") onMergeSkills((state.parsed.skills || []).filter((x) => x && x.trim()));
        if (kind === "experience") onMergeExperience(experienceLines);
        if (kind === "education") onMergeEducation(educationLines);
        toast.success(`Replaced with parsed values.`);
    };

    return (
        <div data-testid="affinda-resume-parser">
            {state.phase === "idle" && (
                <div className="rounded-lg border-2 border-dashed border-border p-6 text-center">
                    <div className="mx-auto w-12 h-12 rounded-xl bg-violet-500/10 text-violet-600 dark:text-violet-400 flex items-center justify-center mb-3">
                        <Sparkles size={20} />
                    </div>
                    <div className="font-heading font-medium">Auto-fill your passport from your resume</div>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                        Drop a PDF or DOCX and our AI will extract your skills, work history, and education so you don&apos;t have to type them.
                    </p>
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="hidden"
                        onChange={(e) => onFile(e.target.files?.[0])}
                        data-testid="affinda-file-input"
                    />
                    <Button
                        onClick={() => inputRef.current?.click()}
                        className="mt-4"
                        data-testid="affinda-upload-btn"
                    >
                        <Upload size={14} className="mr-2" /> Upload resume
                    </Button>
                    <label className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={autoPersonal}
                            onChange={(e) => setAutoPersonal(e.target.checked)}
                            className="rounded border-border"
                            data-testid="affinda-autofill-personal"
                        />
                        Also auto-fill my name &amp; phone (won&apos;t overwrite existing values)
                    </label>
                    <div className="text-[11px] text-muted-foreground mt-2">PDF, DOC, DOCX · up to 10MB</div>
                </div>
            )}

            {state.phase === "uploading" && (
                <div className="rounded-lg border border-border p-6 text-center" data-testid="affinda-uploading">
                    <Loader2 size={22} className="mx-auto animate-spin text-violet-500" />
                    <div className="mt-2 text-sm">Parsing <span className="font-mono text-foreground">{state.filename}</span>…</div>
                    <div className="text-xs text-muted-foreground">This takes 5–15 seconds.</div>
                </div>
            )}

            {state.phase === "ready" && state.parsed && (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-5 space-y-4" data-testid="affinda-results">
                    <div className="flex items-center gap-2">
                        <CheckCheck size={16} className="text-emerald-600" />
                        <div className="flex-1">
                            <div className="font-heading font-medium flex items-center gap-2">
                                Parsed successfully
                                <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 dark:text-emerald-400">
                                    <FileText size={10} className="mr-1" /> {state.filename}
                                </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                                {state.parsed.name && <>Detected name: <b className="text-foreground">{state.parsed.name}</b> · </>}
                                {state.parsed.total_years_experience != null && <>Years of experience: <b className="text-foreground">{state.parsed.total_years_experience}</b></>}
                            </div>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => setState({ phase: "idle", filename: "", parsed: null })} data-testid="affinda-reset">
                            Upload another
                        </Button>
                    </div>

                    {[
                        { key: "skills", label: "Skills", count: state.parsed.skills?.length || 0, preview: state.parsed.skills || [] },
                        { key: "experience", label: "Work experience", count: experienceLines.length, preview: experienceLines },
                        { key: "education", label: "Education", count: educationLines.length, preview: educationLines },
                    ].map((row) => (
                        <div key={row.key} className="rounded-md bg-background/50 border border-border p-3">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium">
                                        {row.label} <span className="text-muted-foreground">({row.count})</span>
                                    </div>
                                    {row.count > 0 ? (
                                        <div className="mt-1 flex flex-wrap gap-1.5">
                                            {row.preview.slice(0, 8).map((p, i) => (
                                                <span key={i} className="text-[11px] bg-muted rounded-md px-2 py-0.5 truncate max-w-[240px]">{p}</span>
                                            ))}
                                            {row.count > 8 && <span className="text-[11px] text-muted-foreground">+{row.count - 8} more</span>}
                                        </div>
                                    ) : (
                                        <div className="text-[11px] text-muted-foreground italic mt-0.5">Nothing detected in this resume.</div>
                                    )}
                                </div>
                                {row.count > 0 && (
                                    <div className="flex items-center gap-1.5">
                                        <Button size="sm" variant="outline" onClick={() => merge(row.key)} data-testid={`affinda-add-${row.key}`}>
                                            <Plus size={12} className="mr-1" /> Add
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => replace(row.key)} data-testid={`affinda-replace-${row.key}`}>
                                            <Replace size={12} className="mr-1" /> Replace
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    <p className="text-[11px] text-muted-foreground text-center">
                        Nothing is auto-saved until you click <b>Add</b> or <b>Replace</b> and then <b>Save changes</b> at the top of the passport.
                    </p>
                </div>
            )}
        </div>
    );
}
